import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ArrowLeft, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const EXP_CATS = ["Admission", "Vehicle rental", "Fuel", "Parking", "Food", "Supplies", "Instructor", "Facility", "Insurance/event cost", "Other"];

export default function AdminExperienceManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [roster, setRoster] = useState([]);
  const [prof, setProf] = useState(null);
  const [chaps, setChaps] = useState([]);
  const [newExp, setNewExp] = useState({ category: "Admission", estimated: "", actual: "" });

  const loadProf = () => api.get(`/admin/experiences/${id}/profitability`).then((r) => setProf(r.data)).catch(() => {});
  const loadChaps = () => api.get(`/admin/experiences/${id}/chaperones`).then((r) => setChaps(r.data)).catch(() => {});

  useEffect(() => {
    api.get(`/admin/experiences/${id}/overview`).then((r) => setOverview(r.data)).catch(() => {});
    api.get(`/admin/experiences/${id}/roster`).then((r) => setRoster(r.data)).catch(() => {});
    loadProf(); loadChaps();
  }, [id]);

  const addExpense = async () => {
    if (!newExp.estimated && !newExp.actual) { toast.error("Enter an estimated or actual amount."); return; }
    try {
      await api.post("/admin/expenses", { experience_id: id, category: newExp.category, estimated: Number(newExp.estimated || 0), actual: Number(newExp.actual || 0) });
      setNewExp({ category: "Admission", estimated: "", actual: "" }); loadProf(); toast.success("Expense added.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delExpense = async (eid) => { await api.delete(`/admin/expenses/${eid}`); loadProf(); };

  const setChapStatus = async (cid, status) => { await api.put(`/admin/chaperones/${cid}`, { status }); loadChaps(); toast.success(`Chaperone ${status}.`); };

  const exp = overview?.experience;

  return (
    <AppShell admin>
      <Button variant="ghost" onClick={() => navigate("/admin/experiences")} className="mb-4 font-display font-semibold text-navy"><ArrowLeft className="mr-2 h-4 w-4" />All Experiences</Button>
      <h1 className="font-display text-3xl font-bold text-navy">{exp?.title}</h1>
      <p className="text-slateBrand">{exp && new Date(exp.date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} • {exp?.location}</p>

      {overview && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {[["Registered", overview.registered], ["Paid", overview.paid], ["Transport", overview.transport_riders], ["Drivers", overview.parent_drivers], ["Chaperones", overview.chaperones], ["Forms Missing", overview.forms_missing], ["Revenue", `$${overview.revenue}`]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-slate-200 bg-white p-3 text-center">
              <div className="font-display text-lg font-bold text-navy">{v}</div>
              <div className="text-[0.65rem] text-slateBrand">{l}</div>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="roster" className="mt-8">
        <TabsList className="bg-white">
          <TabsTrigger value="roster" data-testid="tab-roster">Roster</TabsTrigger>
          <TabsTrigger value="chaperones" data-testid="tab-chaperones">Chaperones</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="tab-profitability">Profitability</TabsTrigger>
        </TabsList>

        <TabsContent value="roster" className="mt-5">
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <Table>
              <TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Guardian</TableHead><TableHead>Payment</TableHead><TableHead>Transport</TableHead><TableHead>Check-In</TableHead><TableHead>Alerts</TableHead></TableRow></TableHeader>
              <TableBody>
                {roster.map((r) => (
                  <TableRow key={r.student_id} data-testid={`roster-row-${r.student_id}`}>
                    <TableCell className="font-semibold text-navy">{r.student_name}<div className="text-xs font-normal text-slateBrand">Grade {r.grade}</div></TableCell>
                    <TableCell className="text-sm">{r.guardian}<div className="text-xs text-slateBrand">{r.guardian_phone}</div></TableCell>
                    <TableCell><StatusBadge status={r.payment_status} /></TableCell>
                    <TableCell className="text-sm capitalize">{r.transport_option}</TableCell>
                    <TableCell className="text-sm">{r.checkin_status || "—"}</TableCell>
                    <TableCell>{(r.allergies || r.accommodations) ? <span className="inline-flex items-center gap-1 text-xs text-alertBrand"><AlertTriangle className="h-3.5 w-3.5" />{[r.allergies, r.accommodations].filter(Boolean).join(", ")}</span> : <span className="text-xs text-slateBrand">None</span>}</TableCell>
                  </TableRow>
                ))}
                {roster.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-slateBrand">No registrations yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="chaperones" className="mt-5">
          <div className="space-y-3">
            {chaps.map((c) => (
              <div key={c.id} data-testid={`chaperone-row-${c.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
                <div><div className="font-display font-semibold text-navy">{c.guardian}</div><StatusBadge status={c.status === "approved" ? "active" : "pending"} label={c.status} /></div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid={`chaperone-approve-${c.id}`} onClick={() => setChapStatus(c.id, "approved")} className="text-successBrand">Approve</Button>
                  <Button size="sm" variant="outline" data-testid={`chaperone-decline-${c.id}`} onClick={() => setChapStatus(c.id, "declined")} className="text-alertBrand">Decline</Button>
                </div>
              </div>
            ))}
            {chaps.length === 0 && <p className="text-sm text-slateBrand">No chaperone volunteers yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="profitability" className="mt-5">
          {prof && (
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="space-y-3 lg:col-span-1">
                {[["Registrations", prof.registrations], ["Gross Revenue", `$${prof.gross_revenue}`], ["Total Expenses", `$${prof.total_expenses}`], ["Gross Profit", `$${prof.gross_profit}`], ["Profit / Student", `$${prof.profit_per_student}`], ["Margin", `${prof.margin_percent}%`], ["Member Revenue", `$${prof.member_revenue}`], ["Transport Revenue", `$${prof.transport_revenue}`]].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4" data-testid={`prof-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className="text-sm text-slateBrand">{l}</span><span className="font-display font-bold text-navy">{v}</span>
                  </div>
                ))}
              </div>
              <div className="lg:col-span-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h3 className="font-display font-bold text-navy">Expenses</h3>
                  <div className="mt-3 space-y-2">
                    {prof.expenses.map((e) => (
                      <div key={e.id} className="flex items-center justify-between rounded-lg bg-cloud p-3 text-sm">
                        <span className="text-navy">{e.category}</span>
                        <div className="flex items-center gap-4"><span className="text-slateBrand">Est ${e.estimated} • Act ${e.actual}</span><button data-testid={`del-expense-${e.id}`} onClick={() => delExpense(e.id)} className="text-alertBrand"><Trash2 className="h-4 w-4" /></button></div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-2 border-t border-slate-100 pt-4">
                    <Select value={newExp.category} onValueChange={(v) => setNewExp({ ...newExp, category: v })}>
                      <SelectTrigger data-testid="expense-category" className="h-11 col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent>{EXP_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid="expense-estimated" type="number" placeholder="Est $" className="h-11" value={newExp.estimated} onChange={(e) => setNewExp({ ...newExp, estimated: e.target.value })} />
                    <Input data-testid="expense-actual" type="number" placeholder="Actual $" className="h-11" value={newExp.actual} onChange={(e) => setNewExp({ ...newExp, actual: e.target.value })} />
                  </div>
                  <Button data-testid="add-expense-btn" onClick={addExpense} className="mt-3 w-full bg-brandblue font-display font-semibold hover:bg-blue-700"><Plus className="mr-2 h-4 w-4" />Add Expense</Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
